import camelcase from 'camelcase'
import decamelizeKeys from 'decamelize-keys'

const LOCALIZED_DATA_KEY = 'localizedData'
const HEADERS_KEY = 'headers'

const REQUEST_EXCLUDED_KEYS = [LOCALIZED_DATA_KEY, 'pageSize'] as const

function isFile(value: unknown): value is File {
  return typeof File !== 'undefined' && value instanceof File
}

function isBlob(value: unknown): value is Blob {
  return typeof Blob !== 'undefined' && value instanceof Blob
}

function isFormData(value: unknown): value is FormData {
  return typeof FormData !== 'undefined' && value instanceof FormData
}

function isUrlSearchParams(value: unknown): value is URLSearchParams {
  return typeof URLSearchParams !== 'undefined' && value instanceof URLSearchParams
}

function isArrayBuffer(value: unknown): value is ArrayBuffer {
  return typeof ArrayBuffer !== 'undefined' && value instanceof ArrayBuffer
}

function isDate(value: unknown): value is Date {
  return value instanceof Date
}

export function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (Object.prototype.toString.call(value) !== '[object Object]') {
    return false
  }

  const prototype: unknown = Object.getPrototypeOf(value as object)
  return prototype === null || prototype === Object.prototype
}

function shouldSkipTransform(value: unknown): boolean {
  return (
    isFormData(value) ||
    isUrlSearchParams(value) ||
    isBlob(value) ||
    isFile(value) ||
    isArrayBuffer(value) ||
    isDate(value)
  )
}

export function toCamelCaseKeys(value: unknown): unknown {
  if (value === null || value === undefined || shouldSkipTransform(value)) {
    return value
  }

  if (Array.isArray(value)) {
    return value.map((item) => toCamelCaseKeys(item))
  }

  if (!isPlainObject(value)) {
    return value
  }

  const transformed = Object.entries(value).reduce<Record<string, unknown>>(
    (acc, [key, nestedValue]) => {
      const normalizedKey = camelcase(key)
      // Giữ nguyên object đặc biệt: localizedData chứa language code, headers chứa tên header case-sensitive như x-ms-blob-type.
      acc[normalizedKey] =
        normalizedKey === LOCALIZED_DATA_KEY || normalizedKey === HEADERS_KEY
          ? nestedValue
          : toCamelCaseKeys(nestedValue)
      return acc
    },
    {},
  )

  return transformed
}

export function toSnakeCaseKeys(value: unknown): unknown {
  if (value === null || value === undefined || shouldSkipTransform(value)) {
    return value
  }

  if (Array.isArray(value)) {
    return value.map((item) => toSnakeCaseKeys(item))
  }

  if (!isPlainObject(value)) {
    return value
  }

  const normalizedEntries = Object.entries(value).map(
    ([key, nestedValue]) => [key, toSnakeCaseKeys(nestedValue)] as const,
  )
  const normalizedObject = Object.fromEntries(normalizedEntries)

  const transformed = decamelizeKeys(normalizedObject, {
    deep: false,
    exclude: [...REQUEST_EXCLUDED_KEYS],
  })

  return transformed
}
